import React from 'react';
import { Tag } from 'antd';
import moment from 'moment';
import type { Route, SummeryData, SummeryLocations } from '../Types/Summary.types';

export const getSuccessRateColor = (rate: number): string => {
  if (rate >= 95) return '#52c41a';
  if (rate >= 90) return '#faad14';
  return '#f5222d';
};

export const percentage = (part: number, whole: number): number => (whole && whole > 0 ? (part / whole) * 100 : 0);

export const ratio = (part: number, whole: number): number => (whole && whole > 0 ? part / whole : 0);

export const formatDateTime = (val: string | null, color: string): JSX.Element => {
  if (!val) return <Tag color={color}>-</Tag>;
  const m = moment(val);
  if (!m.isValid()) return <Tag color={color}>-</Tag>;
  return (
    <Tag color={color}>
      <div>{m.format('MMM DD, YYYY')}</div>
      <div>{m.format('HH:mm:ss')}</div>
    </Tag>
  );
};

export const calculateRouteAdherence = (totalActualDistance: number, optimizedDistance: number): string => {
  if (!totalActualDistance || !optimizedDistance) return 'N/A';

  const adherence = Math.min(1, 2 - totalActualDistance / optimizedDistance) * 100;
  return adherence < 0 ? '0.00%' : `${adherence.toFixed(2)}%`;
};

export type SummeryMetrics = {
  totalOptimumCapacity: number;
  totalUtilizedCapacity: number;
  capacityOptimizationPercent: number;
  avgLocPerRoute: number;
  avgDisPerRoute: number;
  avgDisBetweenLocs: number;
  routeAdherence: string;
  distanceOptimizationPercent: number;
  fuelEfficiencyPercent: number;
};

export const getSummeryMetrics = (data: SummeryData, locations: SummeryLocations, routes: Route[]): SummeryMetrics => {
  const totalOptimumCapacity = routes.reduce((sum, r) => sum + (r.vehicle_capacity || 0), 0);
  const totalUtilizedCapacity = routes.reduce((sum, r) => sum + (r.locations_capacity || 0), 0);

  return {
    totalOptimumCapacity,
    totalUtilizedCapacity,
    capacityOptimizationPercent: percentage(totalUtilizedCapacity, totalOptimumCapacity),
    avgLocPerRoute: ratio(locations.number_of_locations, locations.number_of_routes),
    avgDisPerRoute: ratio(data.total_distance, locations.number_of_routes),
    avgDisBetweenLocs: ratio(data.total_distance, locations.number_of_locations),
    routeAdherence: calculateRouteAdherence(data.total_actual_distance, data.optimized_distance),
    distanceOptimizationPercent: percentage(data.distance_saved, data.total_distance),
    fuelEfficiencyPercent: percentage(data.fuel_saving, data.fuel_consumption),
  };
};
